import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminMileagesApi, useMyCarMileagesApi } from "../../http/mileages/api";
import { UpsertMileage } from "../../http/mileages/types";
import { convertMilesToKilometers } from "../../types/utils";
import ModalDialog from "../ui/ModalDialog";
import MileageForm, { MileageFormBody } from "./MileageForm";

type EditMileageDialogProps = {
  mileageId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
  allowEdit?: boolean;
  carDriverId?: string;
};

const EditMileageDialog: FC<EditMileageDialogProps> = ({
  mileageId,
  onClose,
  carId,
  allowEdit,
  carDriverId,
}: EditMileageDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mileagesAdminApi = useAdminMileagesApi();
  const mileagesApi = useMyCarMileagesApi();

  const distanceInMiles = !!userSettings.me.distanceInMiles;

  const mileageSwr = carDriverId
    ? mileagesApi.getMileage(aborter.current.signal, mileageId, carId, carDriverId)
    : mileagesAdminApi.getMileage(mileageId, aborter.current.signal);

  const [mileage, isInitialized] = useDataInitializer(mileageSwr.data, mileageSwr.error);

  useEffect(() => {
    mileageId && mileageSwr.trigger();
  }, [mileageId]);

  useEffect(() => {
    mileageSwr.error && dispatch(showErrorNotification(i18n, mileageSwr.error));
  }, [mileageSwr.error]);

  const onSubmit = async (formData: MileageFormBody): Promise<void> => {
    if (allowEdit) {
      const body: UpsertMileage = {
        carId: carId ?? formData.car?.id!,
        date: formData.date,
        distance: distanceInMiles ? convertMilesToKilometers(formData.distance!) : formData.distance!,
        currentVersionNumber: mileage?.versionNumber,
      };

      setIsSubmitting(true);
      try {
        if (carDriverId && carId) {
          await mileagesApi.updateMileage(mileageId, body, carId, carDriverId);
        } else {
          await mileagesAdminApi.updateMileage(mileageId, body);
        }
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={allowEdit ? i18n.t.mileages.edit : i18n.t.mileages.singular}>
      <MileageForm
        onSubmit={onSubmit}
        isDisabled={isSubmitting || !allowEdit}
        isLoading={!isInitialized}
        mileage={mileage}
        carId={carId}
        carDriverId={carDriverId}
      />
    </ModalDialog>
  );
};

export default EditMileageDialog;
